/**
 * Tiny client-side persistence for an in-flight CSV import so the rescue can
 * close the wizard and "check back" on a running job. We store the import id
 * (the value `rescueCsvImportProgress(id)` polls) in a short-lived cookie:
 *
 *   - written when an import starts,
 *   - read when the wizard reopens (to resume the progress view), and
 *   - cleared once the job reaches a terminal state while being watched.
 *
 * The cookie's own max-age caps it at 30 minutes, so a job the user never
 * returns to (or one that dies) doesn't leave a stale pointer around. The
 * value is an opaque import id; the backend still authorizes progress reads
 * against the calling reseller, so the cookie alone grants nothing.
 */
import Cookies from 'js-cookie';

const IMPORT_JOB_COOKIE = 'fi_rescue_import_job';
const TTL_MINUTES = 30;

/** The saved import id to resume, or null when none is stored / it has expired. */
export function readImportJobKey(): string | null {
  return Cookies.get(IMPORT_JOB_COOKIE) ?? null;
}

/** Persist the import id to resume, expiring 30 minutes from now. */
export function writeImportJobKey(importId: string): void {
  Cookies.set(IMPORT_JOB_COOKIE, importId, {
    expires: new Date(Date.now() + TTL_MINUTES * 60 * 1000),
    path: '/',
  });
}

/** Drop the saved import id (job finished while watching, or starting fresh). */
export function clearImportJobKey(): void {
  Cookies.remove(IMPORT_JOB_COOKIE, { path: '/' });
}
